/**
 * WebSocket Client for Live Chat Widget
 */

import type { WSMessage } from '../types';

type MessageHandler = (message: WSMessage) => void;
type EventHandler = () => void;

export class WidgetWebSocket {
  private ws: WebSocket | null = null;
  private wsUrl: string;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 2000;
  private isIntentionallyClosed = false;

  private messageHandlers: MessageHandler[] = [];
  private connectHandlers: EventHandler[] = [];
  private disconnectHandlers: EventHandler[] = [];

  constructor(apiUrl: string, conversationId: number, token: string) {
    // Convert HTTP URL to WebSocket URL
    const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
    const baseUrl = apiUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
    this.wsUrl = `${wsProtocol}://${baseUrl}/ws/chat/${conversationId}/?token=${encodeURIComponent(token)}`;
  }

  connect() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('[Widget WS] Already connected');
      return;
    }

    this.isIntentionallyClosed = false;
    
    try {
      console.log('[Widget WS] Connecting to:', this.wsUrl.replace(/token=[^&]+/, 'token=***'));
      this.ws = new WebSocket(this.wsUrl);

      this.ws.onopen = () => {
        console.log('[Widget WS] Connected');
        this.reconnectAttempts = 0;
        this.connectHandlers.forEach(handler => handler());
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WSMessage = JSON.parse(event.data);
          console.log('[Widget WS] Message:', message);
          this.messageHandlers.forEach(handler => handler(message));
        } catch (error) {
          console.error('[Widget WS] Parse error:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('[Widget WS] Error:', error);
      };

      this.ws.onclose = (event) => {
        console.log('[Widget WS] Disconnected:', event.code, event.reason);
        this.disconnectHandlers.forEach(handler => handler());

        // Auto-reconnect with exponential backoff
        if (!this.isIntentionallyClosed && this.reconnectAttempts < this.maxReconnectAttempts) {
          const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts), 30000);
          console.log(`[Widget WS] Reconnecting in ${delay}ms...`);
          setTimeout(() => this.connect(), delay);
          this.reconnectAttempts++;
        }
      };
    } catch (error) {
      console.error('[Widget WS] Connection error:', error);
    }
  }

  disconnect() {
    this.isIntentionallyClosed = true;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  sendTyping(isTyping: boolean) {
    this.send({
      type: 'typing',
      is_typing: isTyping,
    });
  }

  sendReadReceipt(messageIds: string[]) {
    this.send({
      type: 'read_receipt',
      message_ids: messageIds,
    });
  }

  private send(data: any) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn('[Widget WS] Cannot send, not connected:', data);
    }
  }

  onMessage(handler: MessageHandler) {
    this.messageHandlers.push(handler);
  }

  onConnect(handler: EventHandler) {
    this.connectHandlers.push(handler);
  }

  onDisconnect(handler: EventHandler) {
    this.disconnectHandlers.push(handler);
  }
}
